import axios, { AxiosError, type AxiosInstance } from 'axios'
import { useAuthStore } from '@/store/auth-store'
import { useStudentSessionStore } from '@/store/student-session-store'

let isRefreshing = false
let failedQueued: PromiseType[] = []
type SignOut = () => void

type PromiseType = {
  onSucces: (token: string) => void
  onFailure: (error: AxiosError) => void
}

type APIInstanceProps = AxiosInstance & {
  registerInterceptTokenManager: (
    signOut: SignOut,
    type: ApiInstanceType
  ) => () => void
  registerInterceptTokenHeader: (type: ApiInstanceType) => () => void
}

type ApiInstanceType = {
  user: 'manager' | 'student'
}

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 1000000
}) as APIInstanceProps

export const apiStudent = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 1000000
}) as APIInstanceProps

const registerInterceptTokenHeader = (
  type: ApiInstanceType,
  api: APIInstanceProps
) => {
  const interceptTokenHeader = api.interceptors.request.use(async (config) => {
    let token: string | null = null

    if (type.user === 'manager') {
      token = useAuthStore.getState().token
    }

    if (type.user === 'student') {
      token = useStudentSessionStore.getState().token
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    return config
  })

  return () => {
    api.interceptors.request.eject(interceptTokenHeader)
  }
}

const registerInterceptTokenManager = (
  type: ApiInstanceType,
  signOut: SignOut,
  api: APIInstanceProps
) => {
  const interceptTokenManager = api.interceptors.response.use(
    (response) => response,
    async (requestError) => {
      if (requestError?.response?.status === 401) {
        if (requestError?.response?.data?.message === 'expired.token') {
          let refreshToken: string | null = null
          let setToken: (token: string, refreshToken: string) => void

          if (type.user === 'manager') {
            refreshToken = useAuthStore.getState().refreshToken
            setToken = useAuthStore.getState().setToken
          }

          if (type.user === 'student') {
            refreshToken = useStudentSessionStore.getState().refreshToken
            setToken = useStudentSessionStore.getState().setToken
          }

          if (!refreshToken) {
            signOut()
            return Promise.reject(requestError)
          }

          const originalRequestConfig = requestError.config

          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueued.push({
                onSucces: (token: string) => {
                  originalRequestConfig.headers = {
                    Authorization: `Bearer ${token}`
                  }
                  resolve(api(originalRequestConfig))
                },
                onFailure: (error: AxiosError) => {
                  reject(error)
                }
              })
            })
          }

          isRefreshing = true

          // eslint-disable-next-line no-async-promise-executor
          // biome-ignore lint/suspicious/noAsyncPromiseExecutor: <explanation>
          return new Promise(async (resolve, reject) => {
            try {
              let refreshUrl = ''

              if (type.user === 'manager') {
                refreshUrl = 'v1/auth/session/refresh-token'
              }

              if (type.user === 'student') {
                refreshUrl = 'v1/auth/students/sessions/refresh-token'
              }

              const { data } = await api.post(refreshUrl, {
                refreshToken
              })

              const receivedAccessToken = data.data.accessToken
              const receivedRefreshToken = data.data.refreshToken

              setToken(receivedAccessToken, receivedRefreshToken)

              if (typeof originalRequestConfig.data === 'string') {
                try {
                  originalRequestConfig.data = JSON.parse(
                    originalRequestConfig.data
                  )
                } catch {
                }
              }

              originalRequestConfig.headers = {
                ...originalRequestConfig.headers,
                Authorization: `Bearer ${receivedAccessToken}`
              }
              api.defaults.headers.common.Authorization = `Bearer ${receivedAccessToken}`

              failedQueued.forEach((request) => {
                request.onSucces(receivedAccessToken)
              })

              resolve(api(originalRequestConfig))

              // eslint-disable-next-line
            } catch (error: any) {
              failedQueued.forEach((request) => {
                request.onFailure(error)
              })

              signOut()
              reject(error)
            } finally {
              isRefreshing = false
              failedQueued = []
            }
          })
        }
        signOut()
      }

      if (requestError.response && requestError.response.data) {
        return Promise.reject(new Error(requestError.response.data.message))
      } else {
        return Promise.reject(requestError)
      }
    }
  )

  return () => {
    api.interceptors.response.eject(interceptTokenManager)
  }
}

api.registerInterceptTokenHeader = (type: ApiInstanceType) =>
  registerInterceptTokenHeader(type, api)

api.registerInterceptTokenManager = (signOut, type) =>
  registerInterceptTokenManager(type, signOut, api)

apiStudent.registerInterceptTokenHeader = (type: ApiInstanceType) =>
  registerInterceptTokenHeader(type, apiStudent)

apiStudent.registerInterceptTokenManager = (signOut, type: ApiInstanceType) =>
  registerInterceptTokenManager(type, signOut, apiStudent)
